import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useLocale } from '../../shared/i18n/LocaleContext'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import { PageLoading } from '../../shared/components/PageLoading'
import { PasswordInput } from '../../shared/components/PasswordInput'
import { AppModal } from '../../shared/components/AppModal'
import { SortableHeaders, useUrlSortedRows } from '../../shared/components/SortableTableHeader'
import {
  LocalizedDateInput,
  LocalizedDecimalInput,
} from '../../shared/i18n/LocalizedInputs'
import {
  createCompany,
  clearCompanyData,
  createLicense,
  createUser,
  getAdministration,
  resetPassword,
  revokeSession,
  seedCompanyDemoData,
  updateCompany,
  updateLicense,
  updateUser,
  savePermissionRole,
  assignUserRoles,
} from './administrationApi'
import type {
  AdministrationSnapshot,
  CompanyAdministrationItem,
  UserAdministrationItem,
  UserRequest,
} from './types'

type Tab = 'companies' | 'users' | 'roles' | 'licenses' | 'sessions' | 'audit'

const licenseKinds = [
  'Administrator',
  'Integration',
  'FactoryView',
  'Production',
  'Quality',
  'Warehouses',
  'Sales',
  'Purchasing',
  'Finance',
  'Crm',
  'AssistantEssential',
  'AssistantProfessional',
  'AssistantEnterprise',
] as const

const assistantDefaults = {
  AssistantEssential: { monthlySpendLimit: 5, defaultModel: 'gpt-6-luna', allowedModels: ['gpt-6-luna'], reasoningEffort: 'low' },
  AssistantProfessional: { monthlySpendLimit: 25, defaultModel: 'gpt-6-sol', allowedModels: ['gpt-6-luna', 'gpt-6-sol'], reasoningEffort: 'medium' },
  AssistantEnterprise: { monthlySpendLimit: 100, defaultModel: 'gpt-6-astra', allowedModels: ['gpt-6-luna', 'gpt-6-sol', 'gpt-6-astra'], reasoningEffort: 'high' },
} as const
const productKinds = ['Production', 'Quality', 'Warehouses', 'Sales', 'Purchasing', 'Finance', 'Crm'] as const

const emptyUser: UserRequest = {
  userName: '',
  displayName: '',
  culture: 'es-ES',
  licenseKind: 'Basic',
  temporaryPassword: '',
  isSuspended: false,
  companyIds: [],
  productLicenses: ['Production', 'Quality', 'Warehouses', 'Sales', 'Purchasing', 'Finance', 'Crm'],
}

export function AdministrationPage() {
  const { configurationId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user: authenticatedUser, logout } = useAuth()
  const { t, locale, formatDateTime, formatNumber } = useLocale()
  const { confirm, confirmationDialog } = useAppConfirm()
  const [snapshot, setSnapshot] = useState<AdministrationSnapshot | null>(null)
  const [tab, setTab] = useState<Tab>('companies')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'company' | 'user' | 'role' | 'license' | 'password' | null>(null)
  const [passwordForm, setPasswordForm] = useState({ userId: '', password: '', confirmation: '' })
  const [licenseForm, setLicenseForm] = useState<
    AdministrationSnapshot['licenses'][number] | null
  >(null)
  const [companyForm, setCompanyForm] = useState({
    id: '',
    code: '',
    name: '',
    weekStartsOn: 'Monday' as 'Sunday' | 'Monday',
    isActive: true,
  })
  const [userForm, setUserForm] = useState<UserRequest & { id: string }>({
    ...emptyUser,
    id: '',
  })
  const [userRoleIds, setUserRoleIds] = useState<string[]>([])
  const [roleForm, setRoleForm] = useState({
    id: '', name: '', description: '', permissions: {} as Record<string, 'None' | 'Read' | 'Write'>,
  })
  const setEditorRoute = (kind: string, id?: string) => {
    const next = new URLSearchParams(); next.set('editor', kind)
    if (id) next.set('id', id)
    const recordIds = kind === 'company' ? snapshot?.companies.map(value => value.id)
      : kind === 'user' ? snapshot?.users.map(value => value.id)
      : kind === 'role' ? snapshot?.permissionRoles.map(value => value.id)
      : kind === 'license' ? snapshot?.licenses.map(value => value.id)
      : []
    navigate({ search: `?${next}` }, { state: { recordIds } })
  }
  const closeEditor = () => {
    navigate({ search: '' }, { replace: true, state: null })
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setSnapshot(await getAdministration(configurationId))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'administration.loadError')
    } finally {
      setLoading(false)
    }
  }, [configurationId])

  useEffect(() => {
    void load()
  }, [load])

  const execute = async (action: () => Promise<string>, reload = true) => {
    setBusy(true)
    setMessage(null)
    setError(null)
    try {
      setMessage(await action())
      if (reload) await load()
      return true
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'administration.actionError')
      return false
    } finally {
      setBusy(false)
    }
  }

  const submitCompany = async (event: FormEvent) => {
    event.preventDefault()
    const succeeded = await execute(() => companyForm.id
      ? updateCompany(companyForm.id, {
          name: companyForm.name,
          weekStartsOn: companyForm.weekStartsOn,
          isActive: companyForm.isActive,
        }, configurationId)
      : createCompany({
          code: companyForm.code,
          name: companyForm.name,
          weekStartsOn: companyForm.weekStartsOn,
        }, configurationId))
    if (succeeded) {
      setCompanyForm({ id: '', code: '', name: '', weekStartsOn: 'Monday', isActive: true })
      closeEditor()
    }
  }

  const editCompany = (company: CompanyAdministrationItem) => {
    setCompanyForm({
      id: company.id,
      code: company.code,
      name: company.name,
      weekStartsOn: company.weekStartsOn,
      isActive: company.isActive,
    })
    setModal('company')
    setEditorRoute('company', company.id)
  }

  const createDemoData = async (company: CompanyAdministrationItem) => {
    if (!configurationId || !await confirm(
      `${company.name}\n\n${t('administration.seedDemoDataConfirm')}`,
    )) return

    await execute(() => seedCompanyDemoData(company.id, configurationId))
  }

  const clearData = async (company: CompanyAdministrationItem) => {
    if (!configurationId || !await confirm(
      `${company.name}\n\n${t('administration.clearCompanyDataConfirm')}`,
    )) return

    await execute(() => clearCompanyData(company.id, configurationId))
  }

  const submitUser = async (event: FormEvent) => {
    event.preventDefault()
    const request: UserRequest = {
      userName: userForm.userName,
      displayName: userForm.displayName,
      culture: userForm.culture,
      licenseKind: userForm.licenseKind,
      temporaryPassword: userForm.temporaryPassword,
      isSuspended: userForm.isSuspended,
      companyIds: userForm.companyIds,
      productLicenses: userForm.licenseKind === 'Basic' ? userForm.productLicenses : [],
    }
    const succeeded = await execute(async () => {
      const code = await (userForm.id
        ? updateUser(userForm.id, request, configurationId)
        : createUser(request, configurationId))
      if (userForm.id && userForm.licenseKind !== 'Administrator')
        await assignUserRoles(userForm.id, userRoleIds, configurationId)
      return code
    })
    if (succeeded) {
      setUserForm({ ...emptyUser, id: '' })
      closeEditor()
    }
  }

  const editUser = (user: UserAdministrationItem) => {
    setUserForm({
      id: user.id,
      userName: user.userName,
      displayName: user.displayName,
      culture: user.culture,
      licenseKind: user.licenseKind,
      temporaryPassword: '',
      isSuspended: user.isSuspended,
      companyIds: user.companyIds,
      productLicenses: user.productLicenses,
    })
    setUserRoleIds(user.permissionRoleIds)
    setModal('user')
    setEditorRoute('user', user.id)
  }

  const openNewCompany = () => {
    setCompanyForm({ id: '', code: '', name: '', weekStartsOn: 'Monday', isActive: true })
    setModal('company')
    setEditorRoute('company')
  }

  const openNewUser = () => {
    setUserForm({ ...emptyUser, id: '' })
    setUserRoleIds([])
    setModal('user')
    setEditorRoute('user')
  }

  const openPasswordReset = (userId: string, updateRoute = true) => {
    setPasswordForm({ userId, password: '', confirmation: '' })
    setError(null)
    setModal('password')
    if (updateRoute) setEditorRoute('password', userId)
  }

  const returnToUserEditor = () => {
    const userId = passwordForm.userId
    setModal('user')
    setEditorRoute('user', userId)
  }

  const openRole = (role?: AdministrationSnapshot['permissionRoles'][number]) => {
    const permissions = Object.fromEntries((snapshot?.permissionCatalog ?? []).map((code) =>
      [code, role?.permissions[code] ?? 'None'])) as Record<string, 'None' | 'Read' | 'Write'>
    setRoleForm({ id: role?.id ?? '', name: role?.name ?? '', description: role?.description ?? '', permissions })
    setModal('role')
    setEditorRoute('role', role?.id)
  }

  const submitRole = async (event: FormEvent) => {
    event.preventDefault()
    const succeeded = await execute(() => savePermissionRole(roleForm.id || null, {
      name: roleForm.name, description: roleForm.description, permissions: roleForm.permissions,
    }, configurationId))
    if (succeeded) closeEditor()
  }

  const submitPasswordReset = async (event: FormEvent) => {
    event.preventDefault()
    if (passwordForm.password !== passwordForm.confirmation) {
      setError('administration.passwordMismatch')
      return
    }
    const isCurrentUser = passwordForm.userId === authenticatedUser?.id
    const succeeded = await execute(
      () => resetPassword(passwordForm.userId, passwordForm.password, configurationId),
      false,
    )
    if (!succeeded) return
    setPasswordForm({ userId: '', password: '', confirmation: '' })
    closeEditor()
    if (isCurrentUser) {
      await logout()
      return
    }
    await load()
  }

  const openNewLicense = () => {
    setLicenseForm({
      id: '',
      kind: 'Basic',
      quantity: 1,
      status: 'Active',
      startsAtUtc: new Date().toISOString(),
      expiresAtUtc: null,
      monthlyPrice: 0,
      assignedUsers: 0,
      activeSessions: 0,
    })
    setModal('license')
    setEditorRoute('license')
  }

  const editLicense = (
    license: AdministrationSnapshot['licenses'][number],
  ) => {
    setLicenseForm(license)
    setModal('license')
    setEditorRoute('license', license.id)
  }

  const changeCompanyAccess = (companyId: string, checked: boolean) =>
    setUserForm((current) => ({
      ...current,
      companyIds: checked
        ? [...current.companyIds, companyId]
        : current.companyIds.filter((id) => id !== companyId),
    }))

  useEffect(() => {
    const kind = searchParams.get('editor')
    if (!kind) { if (modal) setModal(null); return }
    if (!snapshot) return
    const id = searchParams.get('id')
    if (modal) {
      const currentId = modal === 'company' ? companyForm.id : modal === 'user' ? userForm.id
        : modal === 'role' ? roleForm.id : modal === 'license' ? licenseForm?.id
        : modal === 'password' ? passwordForm.userId
        : null
      if (id && currentId !== id) setModal(null)
      return
    }
    if (kind === 'company') { const item = snapshot.companies.find(value => value.id === id); item ? editCompany(item) : openNewCompany() }
    if (kind === 'user') { const item = snapshot.users.find(value => value.id === id); item ? editUser(item) : openNewUser() }
    if (kind === 'password') { const item = snapshot.users.find(value => value.id === id); if (item) openPasswordReset(item.id, false); else closeEditor() }
    if (kind === 'role') { openRole(snapshot.permissionRoles.find(value => value.id === id)) }
    if (kind === 'license') { const item = snapshot.licenses.find(value => value.id === id); item ? editLicense(item) : openNewLicense() }
  }, [modal, searchParams, snapshot])

  const companySort=useUrlSortedRows(snapshot?.companies??[],{code:x=>x.code,name:x=>x.name,users:x=>x.userCount,status:x=>x.isActive},'code','asc',locale)
  const userSort=useUrlSortedRows(snapshot?.users??[],{code:x=>x.userName,name:x=>x.displayName,license:x=>x.licenseKind,companies:x=>x.companyIds.length,status:x=>x.isSuspended},'code','asc',locale)
  const licenseSort=useUrlSortedRows(snapshot?.licenses??[],{kind:x=>x.kind,quantity:x=>x.quantity,start:x=>x.startsAtUtc,end:x=>x.expiresAtUtc,status:x=>x.status,price:x=>x.monthlyPrice,total:x=>x.monthlyPrice*x.quantity},'kind','asc',locale)
  const roleSort=useUrlSortedRows(snapshot?.permissionRoles??[],{name:x=>x.name,description:x=>x.description,users:x=>x.userIds.length},'name','asc',locale)
  const sessionSort=useUrlSortedRows(snapshot?.sessions??[],{user:x=>x.userName,license:x=>x.licenseKind,updated:x=>x.lastActivityAtUtc},'user','asc',locale)
  const auditSort=useUrlSortedRows(snapshot?.events??[],{updated:x=>x.occurredAtUtc,user:x=>x.actorDisplayName,action:x=>x.action},'updated','desc',locale)

  if (loading && !snapshot) {
    return <PageLoading label={t('administration.loading')}/>
  }

  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">
            {configurationId ? t('aplPanel.eyebrow') : t('administration.title')}
          </span>
          <h1>{snapshot?.configurationName ?? t('administration.title')}</h1>
          <p>{t('administration.subtitle')}</p>
        </div>
      </header>

      {configurationId && <nav className="apl-context-navigation"
        aria-label={t('recordNavigation.backToList')}>
        <Link className="secondary-button" to="/apl/configuraciones">
          ← {t('aplPanel.configurations')}
        </Link>
      </nav>}

      <div className="administration-tabs" role="tablist">
        {(['companies', 'users', 'roles', 'licenses', 'sessions', 'audit'] as Tab[]).map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'active' : undefined}
            key={item}
            onClick={() => setTab(item)}
          >
            {t(`administration.${item}`)}
          </button>
        ))}
      </div>

      {message && <div className="notice success">{t(message as Parameters<typeof t>[0])}</div>}
      {error && !modal && <div className="notice error">{t(error as Parameters<typeof t>[0])}</div>}

      {tab === 'companies' && snapshot && (
          <section className="data-panel administration-panel">
            <div className="section-heading">
              <h2>{t('administration.companies')}</h2>
              <button className="primary-button" type="button" onClick={openNewCompany}>
                + {t('administration.add')}
              </button>
            </div>
            <table className="data-table administration-table">
              <thead><tr>
                <SortableHeaders sort={companySort} columns={[["code",t('aplPanel.code')],["name",t('aplPanel.name')],["users",t('administration.assignedUsers')],["status",t('administration.status')]]}/>
                <th>{t('aplPanel.actions')}</th>
              </tr></thead>
              <tbody>
                {snapshot.companies.length === 0 && <tr><td className="empty-state" colSpan={5}>{t('administration.empty')}</td></tr>}
                {companySort.sortedRows.map((company) => (
                  <tr key={company.id}>
                    <td><button className="record-code-link button-link" type="button" onClick={() => editCompany(company)}>{company.code}</button></td>
                    <td>{company.name}</td>
                    <td>{company.userCount}</td>
                    <td><span className={`status-pill ${company.isActive ? 'active' : 'suspended'}`}>
                      {t(company.isActive ? 'administration.active' : 'administration.inactive')}
                    </span></td>
                    <td>
                      <div className="table-actions">
                        {configurationId && (
                          <>
                            <Link className="table-action" to={
                              `/apl/configuraciones/${configurationId}/empresas/${company.id}/integrator`
                            }>{t('menu.integrator')}</Link>
                            <button disabled={busy || !company.isActive} type="button" onClick={() => void createDemoData(company)}>
                              {t('administration.seedDemoData')}
                            </button>
                            <button className="danger" disabled={busy} type="button" onClick={() => void clearData(company)}>
                              {t('administration.clearCompanyData')}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
      )}

      {tab === 'users' && snapshot && (
          <section className="data-panel administration-panel">
            <div className="section-heading">
              <h2>{t('administration.users')}</h2>
              <button className="primary-button" type="button" onClick={openNewUser}>
                + {t('administration.add')}
              </button>
            </div>
            <table className="data-table administration-table">
              <thead><tr>
                <SortableHeaders sort={userSort} columns={[["code",t('auth.userName')],["name",t('administration.displayName')],["license",t('administration.license')],["companies",t('administration.assignedCompanies')],["status",t('administration.status')]]}/>
              </tr></thead>
              <tbody>
                {snapshot.users.length === 0 && <tr><td className="empty-state" colSpan={5}>{t('administration.empty')}</td></tr>}
                {userSort.sortedRows.map((user) => (
                  <tr key={user.id}>
                    <td><button className="record-code-link button-link" type="button" onClick={() => editUser(user)}>{user.userName}</button></td>
                    <td>{user.displayName}</td>
                    <td>{user.isServiceAccount ? t('administration.userTypeService') : <>
                      {licenseKindLabel(user.licenseKind, t)}
                      {user.licenseKind === 'Basic' && <small>{user.productLicenses
                        .map(product => licenseKindLabel(product, t)).join(' · ')}</small>}
                    </>}</td>
                    <td>{user.companyIds.length}</td>
                    <td><span className={`status-pill ${user.isSuspended ? 'suspended' : 'active'}`}>{t(user.isSuspended ? 'administration.inactive' : 'administration.active')}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
      )}

      {tab === 'licenses' && snapshot && (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t('administration.licenses')}</h2>
            {configurationId && (
              <button className="primary-button" type="button" onClick={openNewLicense}>
                + {t('administration.add')}
              </button>
            )}
          </div>
          <table className={`data-table administration-table license-list-table ${configurationId ? 'editable' : ''}`}>
            <thead><tr>
              <SortableHeaders sort={licenseSort} columns={[["kind",t('administration.licenseKind')],["quantity",t('administration.quantity')],["start",t('administration.startsAt')],["end",t('administration.expiresAt')],["status",t('administration.status')],["price",t('administration.monthlyPrice')],["total",t('administration.monthlyTotal')]]}/>
            </tr></thead>
            <tbody>{licenseSort.sortedRows.map((license) => (
              <tr key={license.id}>
                <td>{configurationId ? <button className="record-code-link button-link" type="button" onClick={() => editLicense(license)}>{licenseKindLabel(license.kind, t)}</button> : <strong>{licenseKindLabel(license.kind, t)}</strong>}</td>
                <td>{formatNumber(license.quantity)}</td>
                <td>{formatListDate(license.startsAtUtc, locale)}</td>
                <td>{license.expiresAtUtc
                  ? formatListDate(license.expiresAtUtc, locale)
                  : '—'}</td>
                <td><span className={`status-pill ${license.status.toLowerCase()}`}>
                  {t(`administration.licenseStatus${license.status}`)}
                </span></td>
                <td>{formatNumber(license.monthlyPrice, { style: 'currency', currency: 'EUR' })}</td>
                <td><strong>{formatNumber(
                  license.monthlyPrice * license.quantity,
                  { style: 'currency', currency: 'EUR' },
                )}</strong></td>
              </tr>
            ))}</tbody>
          </table>
          {snapshot.licenses.length === 0 && (
            <p className="empty-state">{t('administration.empty')}</p>
          )}
        </section>
      )}

      {tab === 'roles' && snapshot && (
        <section className="data-panel administration-panel">
          <div className="section-heading"><div><h2>{t('administration.roles')}</h2><p>{t('administration.rolesHelp')}</p></div>
            <button className="primary-button" type="button" onClick={() => openRole()}>+ {t('administration.add')}</button></div>
          <table className="data-table administration-table"><thead><tr><SortableHeaders sort={roleSort} columns={[["name",t('aplPanel.name')],["description",t('administration.description')],["users",t('administration.assignedUsers')]]}/></tr></thead>
            <tbody>{snapshot.permissionRoles.length === 0 && <tr><td className="empty-state" colSpan={3}>{t('administration.empty')}</td></tr>}{roleSort.sortedRows.map((role) => <tr key={role.id}><td><button className="record-code-link button-link" type="button" onClick={() => openRole(role)}>{role.name}</button></td><td>{role.description || '—'}</td><td>{role.userIds.length}</td></tr>)}</tbody>
          </table>
        </section>
      )}

      {tab === 'sessions' && snapshot && (
        <section className="data-panel administration-panel">
          <div className="section-heading"><h2>{t('administration.sessions')}</h2></div>
          <table className="data-table administration-table">
            <thead><tr><SortableHeaders sort={sessionSort} columns={[["user",t('auth.userName')],["license",t('administration.license')],["updated",t('dashboard.updated')]]}/><th>{t('aplPanel.actions')}</th></tr></thead>
            <tbody>{sessionSort.sortedRows.map((session) => (
              <tr key={session.id}><td><strong>{session.displayName}</strong><small>{session.userName}</small></td><td>{licenseKindLabel(session.licenseKind, t)}</td><td>{formatDateTime(session.lastActivityAtUtc)}</td><td><button className="danger-button" disabled={busy} type="button" onClick={() => void execute(() => revokeSession(session.id, configurationId))}>{t('administration.revoke')}</button></td></tr>
            ))}</tbody>
          </table>
          {snapshot.sessions.length === 0 && <p className="empty-state">{t('administration.empty')}</p>}
        </section>
      )}

      {tab === 'audit' && snapshot && (
        <section className="data-panel administration-panel">
          <div className="section-heading"><h2>{t('administration.audit')}</h2></div>
          <table className="data-table administration-table">
            <thead><tr><SortableHeaders sort={auditSort} columns={[["updated",t('dashboard.updated')],["user",t('auth.userName')],["action",t('aplPanel.actions')]]}/></tr></thead>
            <tbody>{auditSort.sortedRows.map((event) => (
              <tr key={event.id}><td>{formatDateTime(event.occurredAtUtc)}</td><td>{event.actorDisplayName}</td><td>{event.action.startsWith('administration.') ? t(event.action as Parameters<typeof t>[0]) : event.action}</td></tr>
            ))}</tbody>
          </table>
          {snapshot.events.length === 0 && <p className="empty-state">{t('administration.empty')}</p>}
        </section>
      )}

      {modal === 'company' && (
        <AdministrationModal
          title={companyForm.id
            ? t('administration.editCompany')
            : t('administration.createCompany')}
          closeLabel={t('administration.close')}
          saveForm
          saveDisabled={busy}
          error={error ? t(error as Parameters<typeof t>[0]) : null}
          wide
          onClose={closeEditor}
        >
          <form className="administration-form modal-form" onSubmit={submitCompany}>
            <label><span>{t('aplPanel.code')}</span><input required disabled={Boolean(companyForm.id)} value={companyForm.code} onChange={(event) => setCompanyForm({ ...companyForm, code: event.target.value })} /></label>
            <label><span>{t('aplPanel.name')}</span><input required value={companyForm.name} onChange={(event) => setCompanyForm({ ...companyForm, name: event.target.value })} /></label>
            <label><span>{t('administration.weekStartsOn')}</span><select
              value={companyForm.weekStartsOn}
              onChange={(event) => setCompanyForm({
                ...companyForm,
                weekStartsOn: event.target.value as 'Sunday' | 'Monday',
              })}>
              <option value="Monday">{t('capacity.day1')}</option>
              <option value="Sunday">{t('capacity.day0')}</option>
            </select></label>
            {companyForm.id && <label className="checkbox-row"><input type="checkbox" checked={companyForm.isActive} onChange={(event) => setCompanyForm({ ...companyForm, isActive: event.target.checked })} /><span>{t('administration.active')}</span></label>}
            <div className="form-actions">
              <button className="secondary-button" type="button" onClick={closeEditor}>{t('administration.cancel')}</button>
              <button className="primary-button" disabled={busy} type="submit">{t('administration.save')}</button>
            </div>
          </form>
        </AdministrationModal>
      )}

      {modal === 'user' && snapshot && (
        <AdministrationModal
          title={userForm.id
            ? t('administration.editUser')
            : t('administration.createUser')}
          closeLabel={t('administration.close')}
          saveForm
          saveDisabled={busy}
          error={error ? t(error as Parameters<typeof t>[0]) : null}
          wide
          onClose={closeEditor}
        >
          <form className="administration-form modal-form" onSubmit={submitUser}>
            <label><span>{t('auth.userName')}</span><input required disabled={Boolean(userForm.id)} value={userForm.userName} onChange={(event) => setUserForm({ ...userForm, userName: event.target.value })} /></label>
            <label><span>{t('administration.displayName')}</span><input required value={userForm.displayName} onChange={(event) => setUserForm({ ...userForm, displayName: event.target.value })} /></label>
            <div className="compact-form-grid">
              <label><span>{t('user.language')}</span><select value={userForm.culture} onChange={(event) => setUserForm({ ...userForm, culture: event.target.value })}>
                <option value="es-ES">Español</option><option value="en-GB">English</option><option value="fr-FR">Français</option><option value="de-DE">Deutsch</option><option value="it-IT">Italiano</option><option value="pt-PT">Português</option><option value="ca-ES">Català</option>
              </select></label>
              <label><span>{t('administration.userType')}</span><select value={userForm.licenseKind} onChange={(event) => setUserForm({ ...userForm, licenseKind: event.target.value as UserRequest['licenseKind'] })}><option value="Basic">{t('administration.userTypeBasic')}</option><option value="Administrator">{t('administration.userTypeAdministrator')}</option>{(userForm.licenseKind === 'Integration' || snapshot.licenses.some((license) => license.kind === 'Integration' && license.status === 'Active' && license.quantity > 0)) && <option value="Integration">{t('administration.userTypeService')}</option>}</select></label>
            </div>
            {!userForm.id && <label><span>{t('administration.temporaryPassword')}</span><PasswordInput minLength={12} required autoComplete="new-password" value={userForm.temporaryPassword} onChange={(event) => setUserForm({ ...userForm, temporaryPassword: event.target.value })} /></label>}
            <fieldset className="company-access-list"><legend>{t('administration.assignedCompanies')}</legend>
              {snapshot.companies.map((company) => <label className="checkbox-row" key={company.id}><input type="checkbox" checked={userForm.companyIds.includes(company.id)} onChange={(event) => changeCompanyAccess(company.id, event.target.checked)} /><span>{company.code} · {company.name}</span></label>)}
            </fieldset>
            {userForm.licenseKind === 'Basic' && <fieldset className="company-access-list">
              <legend>{t('administration.productLicenses')}</legend>
              {productKinds.map((product) => <label className="checkbox-row" key={product}>
                <input type="checkbox" checked={userForm.productLicenses.includes(product)}
                  onChange={(event) => setUserForm({ ...userForm, productLicenses: event.target.checked
                    ? [...userForm.productLicenses, product]
                    : userForm.productLicenses.filter((item) => item !== product) })} />
                <span>{licenseKindLabel(product, t)}</span>
              </label>)}
              <p className="form-help">{t('administration.productLicensesHelp')}</p>
            </fieldset>}
            {userForm.licenseKind === 'Integration' && <p className="form-help">{t('administration.serviceUserHelp')}</p>}
            {userForm.licenseKind === 'Administrator'
              ? <p className="form-help">{t('administration.administratorFullAccess')}</p>
              : userForm.id && <fieldset className="company-access-list"><legend>{t('administration.roles')}</legend>
                {snapshot.permissionRoles.map((role) => <label className="checkbox-row" key={role.id}><input type="checkbox" checked={userRoleIds.includes(role.id)} onChange={(event) => setUserRoleIds((current) => event.target.checked ? [...current, role.id] : current.filter((id) => id !== role.id))} /><span>{role.name}</span></label>)}
              </fieldset>}
            {userForm.id && <label className="checkbox-row"><input type="checkbox" checked={userForm.isSuspended} onChange={(event) => setUserForm({ ...userForm, isSuspended: event.target.checked })} /><span>{t('administration.inactive')}</span></label>}
            <div className="form-actions">
              {userForm.id && <button className="secondary-button" disabled={busy} type="button" onClick={() => {
                openPasswordReset(userForm.id)
              }}>{t('administration.resetPassword')}</button>}
              <button className="secondary-button" type="button" onClick={closeEditor}>{t('administration.cancel')}</button>
              <button className="primary-button" disabled={busy} type="submit">{t('administration.save')}</button>
            </div>
          </form>
        </AdministrationModal>
      )}

      {modal === 'password' && (
        <AdministrationModal
          title={t('administration.resetPassword')}
          closeLabel={t('administration.close')}
          error={error ? t(error as Parameters<typeof t>[0]) : null}
          onClose={returnToUserEditor}
        >
          <form className="administration-form modal-form" onSubmit={submitPasswordReset}>
            <label><span>{t('administration.temporaryPassword')}</span><PasswordInput autoFocus autoComplete="new-password" minLength={12} required value={passwordForm.password} onChange={(event) => setPasswordForm({ ...passwordForm, password: event.target.value })} /></label>
            <label><span>{t('administration.confirmPassword')}</span><PasswordInput autoComplete="new-password" minLength={12} required value={passwordForm.confirmation} onChange={(event) => setPasswordForm({ ...passwordForm, confirmation: event.target.value })} /></label>
            <p className="form-help">{t('aplPanel.passwordRequirements')}</p>
            {passwordForm.userId === authenticatedUser?.id && <p className="form-warning">{t('administration.selfPasswordResetWarning')}</p>}
            <div className="form-actions">
              <button className="secondary-button" type="button" onClick={returnToUserEditor}>{t('administration.cancel')}</button>
              <button className="primary-button" disabled={busy || passwordForm.password !== passwordForm.confirmation} type="submit">{t('administration.resetPassword')}</button>
            </div>
          </form>
        </AdministrationModal>
      )}
      {modal === 'role' && snapshot && (
        <AdministrationModal title={roleForm.id ? t('administration.editRole') : t('administration.createRole')} closeLabel={t('administration.close')} error={error ? t(error as Parameters<typeof t>[0]) : null} wide onClose={closeEditor} saveForm saveDisabled={busy}>
          <form className="administration-form modal-form" onSubmit={submitRole}>
            <label><span>{t('aplPanel.name')}</span><input required maxLength={120} value={roleForm.name} onChange={(event) => setRoleForm({ ...roleForm, name: event.target.value })} /></label>
            <label><span>{t('administration.description')}</span><textarea maxLength={500} value={roleForm.description} onChange={(event) => setRoleForm({ ...roleForm, description: event.target.value })} /></label>
            <table className="data-table administration-table"><thead><tr><th>{t('administration.functionalArea')}</th><th>{t('administration.permissionLevel')}</th></tr></thead><tbody>
              {snapshot.permissionCatalog.map((code) => <tr key={code}><td>{t(permissionMenuKey(code))}</td><td><select value={roleForm.permissions[code] ?? 'None'} onChange={(event) => setRoleForm({ ...roleForm, permissions: { ...roleForm.permissions, [code]: event.target.value as 'None' | 'Read' | 'Write' } })}><option value="None">{t('administration.permissionNone')}</option><option value="Read">{t('administration.permissionRead')}</option><option value="Write">{t('administration.permissionWrite')}</option></select></td></tr>)}
            </tbody></table>
            <div className="form-actions"><button className="secondary-button" type="button" onClick={closeEditor}>{t('administration.cancel')}</button><button className="primary-button" disabled={busy} type="submit">{t('administration.save')}</button></div>
          </form>
        </AdministrationModal>
      )}

      {modal === 'license' && licenseForm && configurationId && (
        <AdministrationModal
          title={licenseForm.id
            ? t('administration.editLicense')
            : t('administration.createLicense')}
          closeLabel={t('administration.close')}
          error={error ? t(error as Parameters<typeof t>[0]) : null}
          wide
          onClose={closeEditor}
        >
          <LicenseCard
            key={licenseForm.id || 'new'}
            license={licenseForm}
            disabled={busy}
            editable
            isNew={!licenseForm.id}
            save={async (request) => {
              const succeeded = await execute(() => licenseForm.id
                ? updateLicense(licenseForm.id, request, configurationId)
                : createLicense(request, configurationId))
              if (succeeded) closeEditor()
            }}
          />
        </AdministrationModal>
      )}
      {confirmationDialog}
    </main>
  )
}

function AdministrationModal({
  title,
  closeLabel,
  wide = false,
  error,
  onClose,
  saveForm = false,
  saveDisabled = false,
  children,
}: {
  title: string
  closeLabel: string
  wide?: boolean
  error?: ReactNode
  onClose: () => void
  saveForm?: boolean
  saveDisabled?: boolean
  children: ReactNode
}) {
  return <AppModal title={title} closeLabel={closeLabel} onClose={onClose}
    wide={wide} feedback={error} saveForm={saveForm}
    saveDisabled={saveDisabled}>{children}</AppModal>
}

function LicenseCard({
  license,
  disabled,
  editable,
  isNew = false,
  save,
}: {
  license: AdministrationSnapshot['licenses'][number]
  disabled: boolean
  editable: boolean
  isNew?: boolean
  save: (request: {
    kind?: string
    quantity: number
    status: AdministrationSnapshot['licenses'][number]['status']
    startsAtUtc: string
    expiresAtUtc: string | null
    monthlyPrice: number
    assistant?: { monthlySpendLimit: number; defaultModel: string; allowedModels: string[]; reasoningEffort: 'low' | 'medium' | 'high' } | null
  }) => Promise<unknown>
}) {
  const { t, locale, formatNumber } = useLocale()
  const [kind, setKind] = useState(license.kind)
  const [quantity, setQuantity] = useState(license.quantity)
  const [status, setStatus] = useState(license.status)
  const [startsAt, setStartsAt] = useState(toDateInput(license.startsAtUtc))
  const [expiresAt, setExpiresAt] = useState(toDateInput(license.expiresAtUtc))
  const [monthlyPrice, setMonthlyPrice] = useState(license.monthlyPrice)
  const [assistant, setAssistant] = useState<NonNullable<AdministrationSnapshot['licenses'][number]['assistant']> | null>(license.assistant ?? (license.kind in assistantDefaults
    ? { ...assistantDefaults[license.kind as keyof typeof assistantDefaults], allowedModels: [...assistantDefaults[license.kind as keyof typeof assistantDefaults].allowedModels], effectiveMonthlySpendLimit: assistantDefaults[license.kind as keyof typeof assistantDefaults].monthlySpendLimit, monthlySpendLimitEffectiveMonth: null, openAiProvisioned: false, openAiProjectId: null, openAiProvisioningError: null }
    : null))
  const selectKind = (value: string) => {
    setKind(value)
    const defaults = assistantDefaults[value as keyof typeof assistantDefaults]
    setAssistant(defaults ? { ...defaults, allowedModels: [...defaults.allowedModels], effectiveMonthlySpendLimit: assistant?.effectiveMonthlySpendLimit ?? defaults.monthlySpendLimit, monthlySpendLimitEffectiveMonth: assistant?.monthlySpendLimitEffectiveMonth ?? null, openAiProvisioned: assistant?.openAiProvisioned ?? false, openAiProjectId: assistant?.openAiProjectId ?? null, openAiProvisioningError: assistant?.openAiProvisioningError ?? null } : null)
  }
  const modelOptions = [...(assistantDefaults[kind as keyof typeof assistantDefaults]?.allowedModels ?? [])] as string[]
  const maximumModel = modelOptions.includes(assistant?.defaultModel ?? '')
    ? assistant!.defaultModel : (modelOptions.at(-1) ?? '')
  const automaticModels = modelOptions.slice(0, modelOptions.indexOf(maximumModel) + 1)
  return <article className="license-card">
    <div className="license-card-heading">
      <div>
        <span>{isNew
          ? t('administration.createLicense')
          : t('administration.licenseKind')}</span>
        {isNew || assistant
          ? <select className="license-kind-select" value={kind} onChange={(event) => selectKind(event.target.value)}>
              {licenseKinds.filter(item => isNew || item.startsWith('Assistant')).map((item) => (
                <option key={item} value={item}>{licenseKindLabel(item, t)}</option>
              ))}
            </select>
          : <h3>{licenseKindLabel(license.kind, t)}</h3>}
      </div>
      <span className={`status-pill ${status.toLowerCase()}`}>
        {t(`administration.licenseStatus${status}`)}
      </span>
    </div>
    {!isNew && <div className="license-metrics"><span>{t('administration.assignedUsers')}<strong>{license.assignedUsers}</strong></span><span>{t('aplPanel.sessions')}<strong>{license.activeSessions}</strong></span></div>}
    <div className="license-form-grid">
      <label><span>{t('administration.quantity')}</span><input disabled={!editable} type="number" min="0" max="100000" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} /></label>
      <label><span>{t('administration.monthlyPrice')}</span>
        <LocalizedDecimalInput
          disabled={!editable}
          locale={locale}
          value={monthlyPrice}
          min={0}
          max={100000000}
          maximumFractionDigits={2}
          onChange={setMonthlyPrice}
        />
      </label>
      <label><span>{t('administration.startsAt')}</span><LocalizedDateInput disabled={!editable} required locale={locale} ariaLabel={t('administration.startsAt')} pickerLabel={t('input.selectDate')} previousMonthLabel={t('input.previousMonth')} nextMonthLabel={t('input.nextMonth')} value={startsAt} onChange={setStartsAt} /></label>
      <label><span>{t('administration.expiresAt')}</span><LocalizedDateInput disabled={!editable} locale={locale} ariaLabel={t('administration.expiresAt')} pickerLabel={t('input.selectDate')} previousMonthLabel={t('input.previousMonth')} nextMonthLabel={t('input.nextMonth')} value={expiresAt} onChange={setExpiresAt} /></label>
      <label className="license-status-field"><span>{t('administration.status')}</span><select disabled={!editable} value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
        <option value="Active">{t('administration.licenseStatusActive')}</option>
        <option value="Suspended">{t('administration.licenseStatusSuspended')}</option>
        <option value="Cancelled">{t('administration.licenseStatusCancelled')}</option>
        <option value="Expired">{t('administration.licenseStatusExpired')}</option>
      </select></label>
    </div>
    {assistant && <section className="assistant-license-settings">
      <h4>{t('administration.assistantConfiguration')}</h4>
      <div className="assistant-tier-capabilities"><strong>{t('administration.assistantTierCapabilities')}</strong><p>{t(assistantTierDescriptionKey(kind))}</p></div>
      {!isNew && <div className="assistant-tier-capabilities"><strong>{t('administration.assistantEffectiveSpendLimit')}</strong><p>{formatNumber(assistant.effectiveMonthlySpendLimit, { style: 'currency', currency: 'USD' })}</p></div>}
      <div className="license-form-grid">
        <label><span>{t('administration.assistantMonthlySpend')}</span><LocalizedDecimalInput disabled={!editable} locale={locale} value={assistant.monthlySpendLimit} min={0.01} max={1000000} maximumFractionDigits={2} onChange={value => setAssistant({ ...assistant, monthlySpendLimit: value })} /></label>
        <label><span>{t('administration.assistantReasoning')}</span><select disabled={!editable} value={assistant.reasoningEffort} onChange={event => setAssistant({ ...assistant, reasoningEffort: event.target.value as 'low' | 'medium' | 'high' })}><option value="low">{t('administration.assistantReasoningLow')}</option><option value="medium">{t('administration.assistantReasoningMedium')}</option><option value="high">{t('administration.assistantReasoningHigh')}</option></select></label>
        <label><span>{t('administration.assistantDefaultModel')}</span><select disabled={!editable} value={maximumModel} onChange={event => setAssistant({ ...assistant, defaultModel: event.target.value })}>{modelOptions.map(model => <option key={model} value={model}>{model}</option>)}</select></label>
      </div>
      <div className="assistant-model-options"><strong>{t('administration.assistantAllowedModels')}</strong><span>{automaticModels.join(' · ')}</span><small>{t('administration.assistantAutoRouting')}</small></div>
      {!isNew && <div className={`assistant-provisioning-status ${license.assistant?.openAiProvisioned ? 'ready' : 'pending'}`}><strong>{license.assistant?.openAiProvisioned ? t('administration.assistantProjectReady') : t('administration.assistantProjectPending')}</strong>{license.assistant?.openAiProjectId && <code>{license.assistant.openAiProjectId}</code>}{license.assistant?.openAiProvisioningError && <small>{t(license.assistant.openAiProvisioningError as Parameters<typeof t>[0])}</small>}</div>}
    </section>}
    <div className="license-card-footer">
      <span className="license-monthly-total">{t('administration.monthlyTotal')} <strong>{formatNumber(
        monthlyPrice * quantity,
        { style: 'currency', currency: 'EUR' },
      )}</strong></span>
      {editable && <button className="primary-button" disabled={disabled || !startsAt} type="button" onClick={() => void save({
        ...(isNew || assistant ? { kind } : {}),
        quantity,
        status,
        startsAtUtc: new Date(`${startsAt}T00:00:00Z`).toISOString(),
        expiresAtUtc: expiresAt
          ? new Date(`${expiresAt}T23:59:59Z`).toISOString()
          : null,
        monthlyPrice,
        assistant: assistant ? { monthlySpendLimit: assistant.monthlySpendLimit, defaultModel: maximumModel, allowedModels: automaticModels, reasoningEffort: assistant.reasoningEffort } : null,
      })}>{t(isNew ? 'administration.add' : 'administration.save')}</button>}
    </div>
  </article>
}

function permissionMenuKey(code: string) {
  const keys = {
    planning: 'menu.planning', mrp: 'menu.mrp', productionOrders: 'menu.productionOrders', materials: 'menu.materials',
    engineering: 'menu.engineering', capacity: 'menu.resources', quality: 'menu.quality',
    maintenance: 'menu.maintenance', subcontracting: 'subcontracting.title',
    reports: 'menu.reports', notifications: 'menu.notifications',
    factoryView: 'menu.factoryView',
    assistant: 'menu.assistant',
  } as const
  return keys[code as keyof typeof keys] ?? 'administration.functionalArea'
}

function licenseKindLabel(
  kind: string,
  t: ReturnType<typeof useLocale>['t'],
) {
  const keys = {
    Basic: 'administration.licenseKindBasic',
    Administrator: 'administration.licenseKindAdministrator',
    Integration: 'administration.licenseKindIntegration',
    FactoryView: 'administration.licenseKindFactoryView',
    Production: 'administration.licenseKindProduction',
    Quality: 'administration.licenseKindQuality',
    Warehouses: 'administration.licenseKindWarehouses',
    Sales: 'administration.licenseKindSales',
    Purchasing: 'administration.licenseKindPurchasing',
    Finance: 'administration.licenseKindFinance',
    Crm: 'administration.licenseKindCrm',
    AssistantEssential: 'administration.licenseKindAssistantEssential',
    AssistantProfessional: 'administration.licenseKindAssistantProfessional',
    AssistantEnterprise: 'administration.licenseKindAssistantEnterprise',
  } as const
  return kind in keys
    ? t(keys[kind as keyof typeof keys])
    : kind
}

function assistantTierDescriptionKey(kind: string) {
  return (kind === 'AssistantEnterprise' ? 'administration.assistantEnterpriseCapabilities' : kind === 'AssistantProfessional' ? 'administration.assistantProfessionalCapabilities' : 'administration.assistantEssentialCapabilities') as Parameters<ReturnType<typeof useLocale>['t']>[0]
}

function toDateInput(value: string | null) {
  return value ? value.slice(0, 10) : ''
}

function formatListDate(value: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}
